/**
 * A one-key IndexedDB store, which is where the TanStack Query cache is
 * persisted (BP-01 §6 "Shared `lib/`: `offline` — Query persist to IndexedDB").
 *
 * IndexedDB rather than `localStorage` because the cache is structured data and
 * can outgrow the 5 MB synchronous quota, and because reading it must not block
 * the tablet's first paint.
 */

/** The database this app owns. */
export const DB_NAME = '26ad-offline';

/** The single object store inside it. */
export const STORE_NAME = 'query-cache';

/** The single key inside that store. */
export const CACHE_KEY = 'react-query';

/** The schema version; bump it when `STORE_NAME` changes. */
export const DB_VERSION = 1;

/** An async key/value store over one IndexedDB key. */
export interface CacheStorage<T> {
  getItem: () => Promise<T | undefined>;
  setItem: (value: T) => Promise<void>;
  removeItem: () => Promise<void>;
}

/** An `IDBRequest` as a promise. */
export function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB request failed'));
    };
  });
}

/** Open (and, on first use, create) the database. */
export function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  const request = factory.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    request.result.createObjectStore(STORE_NAME);
  };
  return promisify(request);
}

/** Bind `CacheStorage` to an `IDBFactory` (the browser's `indexedDB`). */
export function createIdbStorage<T>(factory: IDBFactory): CacheStorage<T> {
  async function run<R>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<R>,
  ): Promise<R> {
    const database = await openDatabase(factory);
    const store = database.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
    return promisify(operation(store));
  }

  return {
    getItem: () =>
      run<T | undefined>(
        'readonly',
        (store) => store.get(CACHE_KEY) as IDBRequest<T | undefined>,
      ),
    setItem: async (value) => {
      await run('readwrite', (store) => store.put(value, CACHE_KEY));
    },
    removeItem: async () => {
      await run('readwrite', (store) => store.delete(CACHE_KEY));
    },
  };
}
